import { useState, useEffect, useCallback } from "react";
import {
  Trash2,
  Eye,
  Check,
  XCircle,
  Clock
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_ICONS = {
  accepted: <Check className="w-4 h-4" />,
  rejected: <XCircle className="w-4 h-4" />,
  reviewed: <Eye className="w-4 h-4" />,
  pending: <Clock className="w-4 h-4" />
};

const STATUS_LABELS = {
  pending: "En attente",
  reviewed: "Examinée",
  accepted: "Acceptée",
  rejected: "Refusée"
};

const ApplicationsManagement = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchApplications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/applications/`, { withCredentials: true });
      setApplications(response.data);
    } catch (error) {
      console.error("Failed to fetch applications:", error);
      toast.error("Erreur lors du chargement des candidatures");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/applications/${id}/status?status=${status}`, {}, { withCredentials: true });
      toast.success("Statut mis à jour");
      fetchApplications();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm("Supprimer cette candidature ?")) return;
    try {
      await axios.delete(`${API}/applications/${id}`, { withCredentials: true });
      toast.success("Candidature supprimée");
      fetchApplications();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div data-testid="applications-management">
      <h1 className="font-['Outfit'] text-2xl font-bold uppercase mb-8">
        Candidatures
      </h1>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : applications.length === 0 ? (
        <div className="text-center py-12 text-[#94A3B8]">
          Aucune candidature reçue
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="applications-table">
            <thead>
              <tr>
                <th>Candidat</th>
                <th>Contact</th>
                <th>Diplôme</th>
                <th>Salle souhaitée</th>
                <th>Date</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.id} data-testid={`application-row-${app.id}`}>
                  <td>
                    <div className="font-semibold">{app.first_name} {app.last_name}</div>
                    {app.message && (
                      <div className="text-[#94A3B8] text-sm mt-1 max-w-xs truncate" title={app.message}>
                        {app.message}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="text-sm">
                      <div>{app.email}</div>
                      <div className="text-[#94A3B8]">{app.phone}</div>
                    </div>
                  </td>
                  <td className="text-sm">{app.diploma}</td>
                  <td className="text-sm text-[#0066FF]">{app.location}</td>
                  <td className="text-sm text-[#94A3B8]">{formatDate(app.created_at)}</td>
                  <td>
                    <span className={`status-badge status-${app.status}`}>
                      {STATUS_ICONS[app.status] || STATUS_ICONS.pending}
                      <span className="ml-1">{STATUS_LABELS[app.status]}</span>
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <Select onValueChange={(value) => updateStatus(app.id, value)}>
                        <SelectTrigger className="w-[130px] h-8 text-xs bg-[#05050A] border-white/10">
                          <SelectValue placeholder="Changer..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0F0F16] border-white/10">
                          <SelectItem value="pending">En attente</SelectItem>
                          <SelectItem value="reviewed">Examinée</SelectItem>
                          <SelectItem value="accepted">Acceptée</SelectItem>
                          <SelectItem value="rejected">Refusée</SelectItem>
                        </SelectContent>
                      </Select>
                      <button
                        onClick={() => deleteApplication(app.id)}
                        className="p-2 hover:bg-red-500/20 rounded transition-colors text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ApplicationsManagement;
